import React from 'react';

import { FrontendTool, FrontendToolsRegistry } from '@lumapps/agent-chat-frontend-tools/types';

import { RunAgentParameters } from '../../ag-ui';
import { AiAgentInstance } from '../../api';
import { AgentChatContext, AgentChatInstanceValue } from '../../context/AgentChatContext';

/**
 * Context wrapping the app to provide the ability to register frontend tools to give to agents.
 * Tools are registered with the `registerTool` function from the context, and are stored in a registry.
 * We use a ref to avoid whole app re-render, and also expose a subscriber, so components that rely on the
 * tools can subscribe to changes in the registry and update only when necessary.
 */
export const AgentChatProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const instanceRef = React.useRef<AgentChatInstanceValue>({});
    const instanceListenersRef = React.useRef(new Set<() => void>());

    const toolsRef = React.useRef<FrontendToolsRegistry>(new Map());
    const toolListenersRef = React.useRef(new Set<() => void>());

    const setAgentInstance = React.useCallback((instance: AiAgentInstance | undefined) => {
        instanceRef.current = { ...instanceRef.current, agentInstance: instance };
        instanceListenersRef.current.forEach((listener) => listener());
    }, []);

    const setRunAgentParameters = React.useCallback((params: RunAgentParameters | undefined) => {
        instanceRef.current = { ...instanceRef.current, runAgentParameters: params };
        instanceListenersRef.current.forEach((listener) => listener());
    }, []);

    const subscribeInstance = React.useCallback((listener: () => void) => {
        instanceListenersRef.current.add(listener);

        // On unmount, remove the listener from the set
        return () => {
            instanceListenersRef.current.delete(listener);
        };
    }, []);

    const getInstance = React.useCallback(() => instanceRef.current, []);

    const registerTool = React.useCallback(
        <T extends object, C extends object | undefined = undefined>(tool: FrontendTool<T, C>) => {
            const { name: toolName } = tool;
            const next = new Map(toolsRef.current);
            next.set(toolName, tool);
            toolsRef.current = next;
            toolListenersRef.current.forEach((listener) => listener());

            // On unmount, remove the tool from the registry and notify listeners
            return () => {
                const updated = new Map(toolsRef.current);
                updated.delete(toolName);
                toolsRef.current = updated;
                toolListenersRef.current.forEach((listener) => listener());
            };
        },
        [],
    );

    const subscribeTool = React.useCallback((listener: () => void) => {
        toolListenersRef.current.add(listener);

        // On unmount, remove the listener from the set
        return () => {
            toolListenersRef.current.delete(listener);
        };
    }, []);

    const getTools = React.useCallback(() => toolsRef.current, []);

    const contextValue = React.useMemo(
        () => ({
            setAgentInstance,
            setRunAgentParameters,
            subscribeInstance,
            getInstance,
            registerTool,
            subscribeTool,
            getTools,
        }),
        [
            setAgentInstance,
            setRunAgentParameters,
            subscribeInstance,
            getInstance,
            registerTool,
            subscribeTool,
            getTools,
        ],
    );

    return <AgentChatContext.Provider value={contextValue}>{children}</AgentChatContext.Provider>;
};
